import React, { useMemo, useState } from 'react';
import { usePremium } from '../Subscription/PremiumProvider';
import { isLocked } from '../../lib/premiumLock';

/**
 * Compact sidebar stats — formulas match WorkspaceStats.js exactly.
 * Volume Reduction is always visible (no premium gate).
 * Baskets mastery + "N topics remaining" are browse entry points (Phase 4 v6).
 * Locked template baskets/topics are excluded for free users.
 */
const LearnSidebarStats = ({
  baskets = [],
  topics = [],
  unfiledCount = 0,
  onBasketsMasteryClick,
  onUnfiledRemainingClick,
}) => {
  const { isPremium, isLoading } = usePremium();
  const [showMore, setShowMore] = useState(false);

  const countedBaskets = useMemo(
    () => (baskets || []).filter((b) => !isLocked(b, isPremium, isLoading)),
    [baskets, isPremium, isLoading]
  );
  const countedTopics = useMemo(
    () => (topics || []).filter((t) => !isLocked(t, isPremium, isLoading)),
    [topics, isPremium, isLoading]
  );

  // Same as WorkspaceStats.js (on unlocked baskets/topics only for free users)
  const masteredBaskets = countedBaskets.filter(
    (basket) =>
      basket.topics?.length > 0 && basket.topics.every((topic) => topic.is_ripe)
  ).length;

  const masteredTopics = countedTopics.filter((topic) => topic.is_ripe).length;

  const nonEmptyBaskets = countedBaskets.filter(
    (basket) => basket.topics?.length > 0
  );
  const totalTopics = countedTopics.length;
  const groupedTopics = nonEmptyBaskets.reduce(
    (sum, basket) => sum + (basket.topics?.length || 0),
    0
  );

  const ungroupedTopics = countedTopics.length - groupedTopics;
  const finalTotalItems = ungroupedTopics + nonEmptyBaskets.length;

  const volumeReduction =
    totalTopics > 0
      ? Math.round(((totalTopics - finalTotalItems) / totalTopics) * 100)
      : 0;

  const groupingProgress =
    totalTopics > 0 ? Math.round((groupedTopics / totalTopics) * 100) : 0;

  const basketsMasteryPct =
    countedBaskets.length > 0
      ? Math.round((masteredBaskets / countedBaskets.length) * 100)
      : 0;

  const topicsMasteryPct =
    totalTopics > 0 ? Math.round((masteredTopics / totalTopics) * 100) : 0;

  const handleToggleMore = () => {
    setShowMore((prev) => !prev);
  };

  return (
    <div className="space-y-2 px-2" role="group" aria-label="Stats">
      <StatRow
        label="Volume Reduction"
        value={`${volumeReduction}%`}
        caption={`less ${totalTopics - finalTotalItems} topics`}
      />
      <StatRow
        label="Baskets mastery"
        value={`${masteredBaskets}/${countedBaskets.length} · ${basketsMasteryPct}%`}
        onLabelClick={onBasketsMasteryClick}
      />
      <StatRow
        label="Topics mastery"
        value={`${masteredTopics}/${totalTopics} · ${topicsMasteryPct}%`}
      />

      <button
        type="button"
        onClick={handleToggleMore}
        className="text-xs text-neutral-500 hover:text-neutral-700 focus:outline-none focus:ring-1 focus:ring-neutral-400 rounded"
        aria-expanded={showMore}
      >
        {showMore ? '⋯ less' : '⋯ more'}
      </button>

      {showMore && (
        <div className="space-y-2 pt-1">
          <StatRow
            label="Grouping Progress"
            value={`${groupedTopics}/${totalTopics} · ${groupingProgress}%`}
            caption={`${unfiledCount} topics remaining`}
            onCaptionClick={onUnfiledRemainingClick}
          />
        </div>
      )}
    </div>
  );
};

const StatRow = ({ label, value, caption, onLabelClick, onCaptionClick }) => (
  <div className="flex items-start justify-between gap-2">
    {onLabelClick ? (
      <button
        type="button"
        onClick={onLabelClick}
        className="text-xs text-neutral-500 text-left hover:underline hover:text-neutral-700 focus:outline-none focus:ring-1 focus:ring-neutral-400 rounded"
      >
        {label}
      </button>
    ) : (
      <span className="text-xs text-neutral-500">{label}</span>
    )}
    <div className="text-right">
      <span className="text-xs font-semibold text-neutral-800 tabular-nums">
        {value}
      </span>
      {caption &&
        (onCaptionClick ? (
          <button
            type="button"
            onClick={onCaptionClick}
            className="block w-full text-xs text-neutral-500 mt-0.5 hover:underline hover:text-neutral-700 focus:outline-none focus:ring-1 focus:ring-neutral-400 rounded"
          >
            {caption}
          </button>
        ) : (
          <p className="text-xs text-neutral-500 mt-0.5">{caption}</p>
        ))}
    </div>
  </div>
);

export default LearnSidebarStats;
